import React, { SetStateAction, useEffect, useMemo, useState } from "react";
import { RiSearchLine } from "react-icons/ri";
import { useNavigate } from "react-router-dom";
import BaseButton from "Components/Base/BaseButton";
import TableContainer from "Components/Base/BaseTable";
import {
  checkStatusCodeSuccess,
  notFound,
  Search,
} from "Components/constants/Common";
import { NewLabel } from "Components/constants/RepairOrderOpening";
import { listOfROO } from "Api/RepairOrderOpen";
import { toast } from "react-toastify";
import {
  ACTION_LABEL,
  RO_NO_LABEL,
  SR_NO_LABEL,
} from "Components/constants/ROModule";
import { ROReturnLabels } from "Components/constants/ReturnRONPO";
import {
  PART_ISSUE_NO_LABEL,
  PartsIssueDetailsLabel,
} from "Components/constants/PartsIssueDetails";
import { listOfPID } from "Api/PartIssueDetails";
const PartIssueTable = () => {
  const [loader, setLoader] = useState<boolean>(false);
  const [ROO, setROO] = useState<any[]>([]);
  const [searchValue, setSearchValue] = useState<string>("");
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [limit, setLimit] = useState<number>(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [totalNumberOfRows, setTotalNumberOfRows] = useState<number>(0);
  const navigate = useNavigate();
  const [sortOrder, setSortOrder] = useState<string>("desc");
  const [ROOSearchData, setROOSearchData] = useState<Array<any>>([]);
  const [columnName, setColumnName] = useState<string>("id");

  const handleFetchData = (page: any) => {
    setCurrentPage(page);
  };

  const handleFetchSorting = (
    page: any,
    id: SetStateAction<string>,
    order: SetStateAction<string>
  ) => {
    setCurrentPage(page);

    if (id === "repairOrder.ro_no") {
      setColumnName("repairOrder.ro_no");
    } else {
      setColumnName(id);
    }
    setSortOrder(order);
  };

  const fetchROOList = async () => {
    const payload = {
      limit,
      page: currentPage,
      sortKey: columnName,
      sortValue: sortOrder,
    };
    setLoader(true);
    listOfPID(payload)
      .then((res) => {
        if (checkStatusCodeSuccess(res?.statusCode)) {
          setROO(res?.data?.part_issue);
          setTotalRecords(res?.data?.totalItems || 0);
          setTotalPages(res?.data?.totalPages);
          setCurrentPage(res?.data?.currentPage);
          setTotalNumberOfRows(res?.data?.numberOfRows);
        } else {
          setROO([]);
        }
      })
      .catch((err) => {
        toast.error(err?.response?.data?.message || err?.message);
      })
      .finally(() => setLoader(false));
  };

  const fetchSearchROOData = () => {
    const payload = {
      limit,
      page: currentPage,
      search: searchValue,
      sortKey: columnName,
      sortValue: sortOrder,
    };
    setLoader(true);
    listOfROO(payload)
      .then((response) => {
        if (checkStatusCodeSuccess(response.statusCode)) {
          setROOSearchData(response?.data?.repairOrder || []);
          setTotalRecords(response?.data?.totalItems || 0);
          setTotalPages(response?.data?.totalPages);
          setCurrentPage(response?.data?.currentPage);
          setTotalNumberOfRows(response?.data?.numberOfRows);
          toast.success(response?.message);
        } else {
          setROOSearchData([]);
        }
      })
      .catch((error) => {
        setROOSearchData([]);
      })
      .finally(() => {
        setLoader(false);
      });
  };

  useEffect(() => {
    if (searchValue) {
      fetchSearchROOData();
    } else {
      fetchROOList();
    }
  }, [currentPage, limit, searchValue, sortOrder, columnName]);

  const repairOrderColums = useMemo(
    () => [
      {
        header: SR_NO_LABEL,
        accessorKey: "srNo",
        cell: (info: any) => info.row.index + 1,
        flex: 1.5,
        minWidth: 150,
        enableColumnFilter: false,
        enableSorting: false,
      },
      {
        header: RO_NO_LABEL,
        accessorKey: "repairOrder.ro_no",
        enableSorting: true,
        enableColumnFilter: false,
      },
      {
        header: PART_ISSUE_NO_LABEL,
        accessorKey: "pi_no",
        enableSorting: true,
        enableColumnFilter: false,
      },
      {
        header: ACTION_LABEL,
        accessorKey: "ActionKey",
        cell: (cell: any) => (
          <div className="d-flex justify-content-start enable-action  gap-2 icon">
            <span className="tooltip-container">
              <i
                className="mdi mdi-eye-outline fs-4 text-warning cursor-pointer"
                onClick={() => {
                  navigate(
                    `/viewPartIssueDetailsForm/${cell?.row?.original?.repairOrder?.id}`,
                    {
                      state: { viewQuotation: true },
                    }
                  );
                }}
              ></i>
              <span className="tooltip-text">{ROReturnLabels.View}</span>
            </span>
            <span className="tooltip-container">
              <i
                className="mdi mdi-history fs-4 text-primary cursor-pointer"
                onClick={() => {
                  navigate(`/partIssueHistory/${cell?.row?.original?.id}`);
                }}
              ></i>
              <span className="tooltip-text">{ROReturnLabels.History}</span>
            </span>
            <span className="tooltip-container">
              <i
                className="mdi mdi-pencil-outline fs-4 text-info cursor-pointer"
                onClick={() =>
                  navigate(
                    `/editPartIssueDetailsForm/${cell?.row?.original?.repairOrder?.id}`
                  )
                }
              ></i>
              <span className="tooltip-text">{ROReturnLabels.Edit}</span>
            </span>
          </div>
        ),
        enableColumnFilter: false,
        enableSorting: false,
      },
    ],
    []
  );
  document.title = PartsIssueDetailsLabel;

  return (
    <div className="page-content">
      <div className="flex-grow-1 mx-3">
        <div className="mb-1 mx-2 d-flex justify-content-between align-items-center">
          <span className="fs-4 d-flex gap-4">{PartsIssueDetailsLabel}</span>
          <div className="d-flex align-items-center gap-2">
            <BaseButton
              className="btn btn-success"
              onClick={() => navigate("/partIssueDetailsForm")}
              children={
                <>
                  <i className="mdi mdi-plus" /> {NewLabel}
                </>
              }
            />
          </div>
        </div>

        <div className="card py-2 px-4 overflow-x-auto">
          {loader || ROO.length > 0 || ROOSearchData.length > 0 ? (
            <TableContainer
              customPageSize={limit}
              setCustomPageSize={setLimit}
              isGlobalFilter={true}
              columns={repairOrderColums}
              data={searchValue ? ROOSearchData : ROO || []}
              tableClass="table bg-white"
              manualPagination={true}
              divClass="text-nowrap"
              manualFiltering={true}
              tableLoader={loader}
              hasManualPagination={true}
              SearchPlaceholder={Search}
              currentPage={currentPage}
              setCurrentPage={setCurrentPage}
              totalPages={totalPages}
              totalRecords={totalRecords}
              totalNumberOfRows={totalNumberOfRows}
              onSearch={setSearchValue}
              fetchData={handleFetchData}
              fetchSortingData={handleFetchSorting}
            />
          ) : (
            <div className="py-4 text-center">
              <div>
                <span className="fs-1 text-success">
                  <RiSearchLine />
                </span>
              </div>
              <div className="mt-4">
                <h5>{notFound.dataNotFound}</h5>
                <p className="text-muted">{notFound.thereIsNoDataFound}</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PartIssueTable;
